import { PropsWithChildren, useState } from 'react';
import { use100vh } from 'react-div-100vh';
import { FiChevronLeft, FiMenu, FiX } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

import MobileMenu from './MobileMenu';

import { AnimatePresenceComponent } from '@/components/AnimatePresenceComponent';
import { PropsNavBar } from '@/components/Navbar';
import MenuLogo from '@/components/Navbar/MenuLogo';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import { cn } from '@/utils';

const menuActionClass =
  'rounded-[.63rem] bg-gdt-gray-9 w-8 h-8 flex justify-center items-center';

const FaqsNavBar = ({
  children,
  mobile,
  desktop,
  className,
  heightAuto = undefined,
  userLogged,
}: PropsWithChildren<PropsNavBar>) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const height = use100vh();
  const [toggleMobileMenu, setToggleMobileMenu] = useState(false);
  const navigate = useNavigate();

  const handleToggleMobileMenu = (val?: boolean) => {
    setToggleMobileMenu(val ?? !toggleMobileMenu);
  };

  if (mobile && isMobile) {
    const { centerContent, leftContent, rightContent } = mobile;

    const renderMobileLeftContent = () => {
      if (leftContent) {
        if (Object.keys(leftContent).length > 2) {
          throw new Error(
            "You can't use more than two property in leftContent"
          );
        }

        if ('backAction' in leftContent) {
          return (
            <button
              className={menuActionClass}
              type="button"
              onClick={leftContent.backAction}
            >
              <FiChevronLeft className="text-white w-6 h-6" />
            </button>
          );
        } else if ('showMenuAction' in leftContent) {
          const isItemsInLeftContent = 'items' in leftContent;

          if (!isItemsInLeftContent) {
            throw new Error(
              'If you are using showMenuAction you need to pass items in leftContent'
            );
          }

          return (
            <button
              className={menuActionClass}
              type="button"
              onClick={() => handleToggleMobileMenu()}
            >
              <FiMenu className="text-white w-4 h-4" />
            </button>
          );
        } else if ('displayLogo' in leftContent) {
          return <MenuLogo />;
        } else {
          return null;
        }
      }
      return null;
    };

    const renderMobileCenterContent = () => {
      if (centerContent) {
        if (Object.keys(centerContent).length > 1) {
          if ('displayLogo' in centerContent) {
            throw new Error(
              "You can't use displayLogo when you have more than one property in centerContent"
            );
          }
        }

        if ('title' in centerContent) {
          return (
            <div className="space-y-0">
              <p className="text-base text-white text-center font-semibold">
                {centerContent.title}
              </p>
              {centerContent.subTitle && (
                <p className="text-3xs text-white text-center opacity-70 uppercase">
                  {centerContent.subTitle}
                </p>
              )}
            </div>
          );
        } else if ('displayLogo' in centerContent) {
          return <MenuLogo />;
        } else {
          return null;
        }
      }
      return null;
    };

    const renderMobileRightContent = () => {
      if (rightContent) {
        if (Object.keys(rightContent).length > 1) {
          throw new Error(
            "You can't use more than one property in rightContent"
          );
        }

        if ('customRightContent' in rightContent) {
          return rightContent.customRightContent;
        } else if ('closeMenuActionUrl' in rightContent) {
          return (
            <Link
              className={menuActionClass}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              to={rightContent.closeMenuActionUrl!}
            >
              <FiX className="text-white w-5 h-5" />
            </Link>
          );
        } else if ('customRightAction' in rightContent) {
          return (
            <button
              className={menuActionClass}
              onClick={() => rightContent.customRightAction()}
            >
              <FiX className="text-white w-5 h-5" />
            </button>
          );
        } else {
          return null;
        }
      }
      return null;
    };

    return (
      <>
        {leftContent && 'items' in leftContent && (
          <MobileMenu
            handleOpen={(val: boolean | undefined) =>
              handleToggleMobileMenu(val)
            }
            isOpen={toggleMobileMenu}
          />
        )}
        <AnimatePresenceComponent isLoaded={true}>
          <div
            className="flex flex-col"
            style={{ minHeight: heightAuto || `${height}px` }}
          >
            <nav
              className={cn(
                'z-10 sticky top-0 min-h-[51px] ps-5 pe-6 flex lg:hidden bg-gdt-blue-header justify-between items-center',
                className
              )}
            >
              <figure className="w-9 flex justify-center">
                {renderMobileLeftContent()}
              </figure>
              <div className="flex-1">{renderMobileCenterContent()}</div>
              <div className="w-9 flex justify-center relative">
                {renderMobileRightContent()}
              </div>
            </nav>
            <div
              className="overflow-auto flex-1 flex flex-col"
              style={{ minHeight: heightAuto || `calc(${height}px - 51px)` }}
            >
              {children}
            </div>
          </div>
        </AnimatePresenceComponent>
      </>
    );
  }

  if (desktop && isDesktop) {
    return (
      <div
        className="flex flex-col"
        style={{
          minHeight: heightAuto || `calc(${height}px + 70px)`,
        }}
      >
        <nav
          className={cn(
            'bg-gdt-blue-header min-h-[70px] w-full relative items-center justify-between px-8 lg:flex hidden navDesktop'
          )}
        >
          <div className="flex flex-1 items-center justify-between sm:items-stretch sm:justify-start gap-9">
            <div>
              <MenuLogo className="max-w-[30px] min-w-[30px] w-full" />
            </div>
            <div className="flex-1" />
          </div>
          <div className="flex items-center cursor-pointer">
            <button
              className="text-white text-sm font-semibold cursor-pointer"
              type="button"
              onClick={() => {
                if (userLogged) {
                  window.close();
                } else {
                  navigate(ROUTES.basePath);
                }
              }}
            >
              {userLogged ? 'VOLVER' : 'INGRESAR'}
            </button>
          </div>
        </nav>
        <AnimatePresenceComponent isLoaded={true}>
          <div
            style={{
              minHeight: heightAuto || `calc(${height}px - 70px)`,
            }}
          >
            {children}
          </div>
        </AnimatePresenceComponent>
      </div>
    );
  }

  return (
    <AnimatePresenceComponent isLoaded={true}>
      <div
        className="flex-1 flex"
        style={{
          minHeight: heightAuto || `calc(${height}px - 86px)`,
        }}
      >
        {children}
      </div>
    </AnimatePresenceComponent>
  );
};

export default FaqsNavBar;
